import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Download, Plus, X, FileBarChart2, Scale } from "lucide-react";
import DateInput from "@/components/DateInput";

const PERIOD_PRESETS = [
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "ytd", label: "Year to date" },
  { value: "last_year", label: "Last year" },
  { value: "custom", label: "Custom range…" },
];

function money(v) {
  const n = Number(v || 0);
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

export default function Reports() {
  const [tab, setTab] = useState("pnl");

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto space-y-6" data-testid="reports-page">
      <div>
        <div className="overline text-forest">Reports</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mt-1">Financial statements</h1>
        <p className="text-ink-light mt-2 max-w-xl text-sm">
          Generate a P&amp;L statement or balance sheet — view on-screen or download as a branded PDF.
        </p>
      </div>

      <div className="flex gap-1 border-b border-border" data-testid="reports-tabs">
        <TabButton active={tab === "pnl"} onClick={() => setTab("pnl")} testid="tab-pnl" icon={FileBarChart2}>
          P&amp;L Statement
        </TabButton>
        <TabButton active={tab === "bs"} onClick={() => setTab("bs")} testid="tab-bs" icon={Scale}>
          Balance Sheet
        </TabButton>
      </div>

      {tab === "pnl" ? <PnLPanel /> : <BalanceSheetPanel />}
    </div>
  );
}

function TabButton({ active, onClick, testid, icon: Icon, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 text-sm font-medium transition-colors ${
        active ? "border-forest text-forest" : "border-transparent text-ink-light hover:text-ink"
      }`}
    >
      <Icon className="w-4 h-4" strokeWidth={1.75} />
      {children}
    </button>
  );
}

// -------------- P&L PANEL --------------

function PnLPanel() {
  const [period, setPeriod] = useState("ytd");
  const [start, setStart] = useState(firstOfMonthIso());
  const [end, setEnd] = useState(todayIso());
  const [compare, setCompare] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (period === "custom") {
        params.set("start", start);
        params.set("end", end);
      }
      if (compare) params.set("compare", "1");
      const { data: d } = await api.get(`/reports/pnl?${params.toString()}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load P&L");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [compare]);

  const download = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ period });
      if (period === "custom") {
        params.set("start", start);
        params.set("end", end);
      }
      if (compare) params.set("compare", "1");
      const res = await api.get(`/reports/pnl/pdf?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pnl-${data?.start || "report"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="card-surface p-5 flex flex-wrap items-end gap-4" data-testid="pnl-controls">
        <div>
          <label className="text-xs font-medium text-ink-light uppercase tracking-wide">Period</label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            data-testid="pnl-period-select"
            className="input-base mt-1 w-48"
          >
            {PERIOD_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        {period === "custom" && (
          <>
            <div>
              <label className="text-xs font-medium text-ink-light uppercase tracking-wide">Start</label>
              <DateInput
                value={start}
                onChange={setStart}
                data-testid="pnl-start-input"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-ink-light uppercase tracking-wide">End</label>
              <DateInput
                value={end}
                onChange={setEnd}
                data-testid="pnl-end-input"
                className="mt-1"
              />
            </div>
          </>
        )}
        <button onClick={load} disabled={loading} data-testid="pnl-run-button" className="btn-primary inline-flex items-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Generate P&amp;L
        </button>
        <button onClick={download} disabled={!data || downloading} data-testid="pnl-download-button"
          className="btn-secondary inline-flex items-center gap-2">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download PDF
        </button>
        <label
          className={`ml-auto inline-flex items-center gap-2 text-xs cursor-pointer select-none rounded-full border px-2.5 py-1.5 transition-colors ${
            compare ? "bg-ochre/10 border-ochre text-ochre" : "border-border text-ink-light hover:bg-paper hover:text-ink"
          }`}
          data-testid="pnl-compare-label"
        >
          <input
            type="checkbox"
            className="sr-only"
            checked={compare}
            onChange={(e) => setCompare(e.target.checked)}
            data-testid="pnl-compare-toggle"
          />
          <span className={`w-2 h-2 rounded-full ${compare ? "bg-ochre" : "bg-ink-lighter/50"}`} aria-hidden="true" />
          Compare to prior period
        </label>
      </div>

      {data && (
        <div className="space-y-6" data-testid="pnl-result">
          <div className="text-sm text-ink-light">
            {data.period_label} · {data.txn_count} transactions
            {data.compare && (
              <span className="ml-2 text-ochre" data-testid="pnl-compare-hint">
                · vs prior {data.compare.prior.period_label}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SummaryTile
              label="Revenue"
              value={money(data.total_revenue)}
              accent="text-forest"
              testid="pnl-revenue"
              priorValue={data.compare ? money(data.compare.prior.total_revenue) : null}
              deltaPct={data.compare?.delta_revenue_pct ?? null}
            />
            <SummaryTile
              label="Expenses"
              value={money(data.total_expenses)}
              accent="text-ochre"
              testid="pnl-expenses"
              priorValue={data.compare ? money(data.compare.prior.total_expenses) : null}
              deltaPct={data.compare?.delta_expenses_pct ?? null}
              invertDelta
            />
            <SummaryTile
              label="Net Income"
              value={money(data.net_income)}
              accent={data.net_income >= 0 ? "text-forest" : "text-red-600"}
              sub={data.gross_margin_pct != null ? `Margin ${data.gross_margin_pct}%` : null}
              testid="pnl-net"
              priorValue={data.compare ? money(data.compare.prior.net_income) : null}
              deltaPct={data.compare?.delta_net_pct ?? null}
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CategoryList
              title="Revenue"
              rows={data.revenue_rows}
              total={data.total_revenue}
              testid="pnl-revenue-list"
              priorRows={data.compare?.prior.revenue_rows}
              priorTotal={data.compare?.prior.total_revenue}
            />
            <CategoryList
              title="Expenses"
              rows={data.expense_rows}
              total={data.total_expenses}
              testid="pnl-expenses-list"
              priorRows={data.compare?.prior.expense_rows}
              priorTotal={data.compare?.prior.total_expenses}
              invertDelta
            />
          </div>
        </div>
      )}
    </div>
  );
}

function fmtDeltaPct(pct) {
  if (pct == null) return null;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct}%`;
}

function deltaClass(pct, invert) {
  if (pct == null || pct === 0) return "text-ink-light bg-ink-lighter/10";
  const good = (pct > 0) !== !!invert; // XOR
  return good ? "text-forest bg-forest/10" : "text-destructive bg-destructive/10";
}

function pctDelta(cur, prev) {
  if (prev == null) return null;
  if (prev === 0) return cur === 0 ? 0 : cur > 0 ? 100 : -100;
  return Math.round(((cur - prev) / Math.abs(prev)) * 1000) / 10;
}

function SummaryTile({ label, value, accent, sub, testid, priorValue, deltaPct, invertDelta }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`num text-3xl font-medium mt-2 ${accent || ""}`}>{value}</div>
      {sub && <div className="text-xs text-ink-light mt-1">{sub}</div>}
      {priorValue != null && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border text-xs">
          <span className="text-ink-lighter">Prior:</span>
          <span className="num text-ink-light">{priorValue}</span>
          {deltaPct != null && (
            <span
              className={`ml-auto inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${deltaClass(deltaPct, invertDelta)}`}
              data-testid={`${testid}-delta`}
            >
              {fmtDeltaPct(deltaPct)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function CategoryList({ title, rows, total, testid, priorRows, priorTotal, invertDelta }) {
  const compare = priorRows != null;
  const priorMap = compare
    ? Object.fromEntries(priorRows.map((r) => [r.category, r.amount]))
    : {};
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="font-heading text-lg font-medium mb-3">{title}</div>
      {rows.length === 0 && !compare ? (
        <div className="text-sm text-ink-lighter">No entries in this period.</div>
      ) : (
        <div>
          {compare && (
            <div className="grid grid-cols-[1fr_80px_80px_60px] gap-2 text-[10px] uppercase tracking-wider text-ink-lighter pb-2 border-b border-border">
              <span>Category</span>
              <span className="text-right">Prior</span>
              <span className="text-right">Current</span>
              <span className="text-right">Δ</span>
            </div>
          )}
          <div className="divide-y divide-border">
            {rows.map((r) => {
              const prev = priorMap[r.category] || 0;
              const d = compare ? pctDelta(r.amount, prev) : null;
              return (
                <div
                  key={r.category}
                  className={compare ? "grid grid-cols-[1fr_80px_80px_60px] gap-2 py-2 text-sm items-center" : "flex items-center justify-between py-2 text-sm"}
                >
                  <span className="text-ink truncate">{r.category}</span>
                  {compare && <span className="num text-ink-light text-right text-xs">{money(prev)}</span>}
                  <span className={`num text-ink ${compare ? "text-right text-xs" : ""}`}>{money(r.amount)}</span>
                  {compare && (
                    <span
                      className={`text-right text-[10px] font-medium px-1 py-0.5 rounded ${deltaClass(d, invertDelta)}`}
                    >
                      {fmtDeltaPct(d)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className={compare ? "grid grid-cols-[1fr_80px_80px_60px] gap-2 pt-3 mt-2 border-t-2 border-ink text-sm font-medium items-center" : "flex items-center justify-between pt-3 mt-2 border-t-2 border-ink text-sm font-medium"}>
            <span>Total {title}</span>
            {compare && <span className="num text-ink-light text-right text-xs">{money(priorTotal)}</span>}
            <span className={`num ${compare ? "text-right text-xs" : ""}`}>{money(total)}</span>
            {compare && (
              <span
                className={`text-right text-[10px] font-medium px-1 py-0.5 rounded ${deltaClass(pctDelta(total, priorTotal), invertDelta)}`}
              >
                {fmtDeltaPct(pctDelta(total, priorTotal))}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// -------------- BALANCE SHEET PANEL --------------

function BalanceSheetPanel() {
  const [asOf, setAsOf] = useState(todayIso());
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [bs, its] = await Promise.all([
        api.get(`/reports/balance-sheet?as_of=${asOf}`),
        api.get(`/reports/balance-sheet/items`),
      ]);
      setData(bs.data);
      setItems(its.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load balance sheet");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [asOf]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/reports/balance-sheet/pdf?as_of=${asOf}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `balance-sheet-${asOf}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/reports/balance-sheet/items/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="card-surface p-5 flex flex-wrap items-end gap-4" data-testid="bs-controls">
        <div>
          <label className="text-xs font-medium text-ink-light uppercase tracking-wide">As of</label>
          <DateInput value={asOf} onChange={setAsOf} data-testid="bs-asof-input" className="mt-1" />
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="bs-add-item-button" className="btn-secondary inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add asset / liability / equity
        </button>
        <button onClick={download} disabled={!data || downloading} data-testid="bs-download-button"
          className="btn-primary inline-flex items-center gap-2 ml-auto">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download PDF
        </button>
      </div>

      {loading && <div className="text-sm text-ink-light flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}

      {data && (
        <div data-testid="bs-result" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SummaryTile label="Total Assets" value={money(data.total_assets)} accent="text-forest" testid="bs-total-assets" />
            <SummaryTile label="Total Liabilities" value={money(data.total_liabilities)} accent="text-ochre" testid="bs-total-liabilities" />
            <SummaryTile label="Total Equity" value={money(data.total_equity)} accent="text-ink" testid="bs-total-equity" />
          </div>

          <div className={`card-surface p-4 flex items-center justify-between text-sm ${data.balanced ? "" : "!bg-amber-50 border-amber-200"}`} data-testid="bs-balance-check">
            <span className="font-medium">Assets = Liabilities + Equity</span>
            <span className="num">
              {money(data.total_assets)} = {money(data.total_liabilities + data.total_equity)}
              {!data.balanced && <span className="ml-2 text-amber-700">(needs review)</span>}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <BSSection title="Assets" groups={data.assets} total={data.total_assets} onDelete={removeItem} testid="bs-assets" />
            <BSSection title="Liabilities" groups={data.liabilities} total={data.total_liabilities} onDelete={removeItem} testid="bs-liabilities" />
            <BSSection title="Equity" groups={data.equity} total={data.total_equity} onDelete={removeItem} testid="bs-equity" />
          </div>
        </div>
      )}

      {showAdd && <AddItemModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function BSSection({ title, groups, total, onDelete, testid }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="font-heading text-lg font-medium mb-3">{title}</div>
      {groups.length === 0 ? (
        <div className="text-sm text-ink-lighter">Nothing here yet.</div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.category}>
              <div className="text-xs font-medium text-ink-light uppercase tracking-wide mb-1">{g.category}</div>
              <div className="divide-y divide-border">
                {g.items.map((it) => (
                  <div key={`${it.id || it.name}-${it.amount}`} className="flex items-center justify-between py-2 text-sm gap-2">
                    <span className="text-ink flex-1 truncate" title={it.name}>
                      {it.name}
                      {it.auto && <span className="ml-2 text-[10px] uppercase tracking-wide text-forest bg-forest/10 px-1.5 py-0.5 rounded">auto</span>}
                    </span>
                    <span className="num text-ink whitespace-nowrap">{money(it.amount)}</span>
                    {!it.auto && (
                      <button
                        onClick={() => onDelete(it.id)}
                        data-testid={`bs-delete-${it.id}`}
                        className="text-ink-lighter hover:text-red-600 p-1 -mr-1"
                        aria-label={`Delete ${it.name}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2 mt-1 border-t border-border text-xs text-ink-light">
                <span>Subtotal</span>
                <span className="num">{money(g.subtotal)}</span>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-3 mt-2 border-t-2 border-ink text-sm font-medium">
            <span>Total {title}</span>
            <span className="num">{money(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function AddItemModal({ onClose, onSaved }) {
  const [type, setType] = useState("asset");
  const [category, setCategory] = useState("");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const suggested = {
    asset: ["Fixed Assets", "Accounts Receivable", "Inventory", "Prepaid Expenses"],
    liability: ["Accounts Payable", "Accrued Expenses", "Taxes Payable", "Notes Payable"],
    equity: ["Owner Contributions", "Owner Draws", "Common Stock"],
  }[type] || [];

  const save = async () => {
    if (!category.trim() || !name.trim() || amount === "" || isNaN(Number(amount))) {
      toast.error("Fill category, name, and a valid amount");
      return;
    }
    setSaving(true);
    try {
      await api.post("/reports/balance-sheet/items", {
        type, category: category.trim(), name: name.trim(), amount: Number(amount),
      });
      toast.success("Added");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="bs-add-modal">
      <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-medium">Add balance-sheet item</h3>
          <button onClick={onClose} className="text-ink-lighter hover:text-ink" data-testid="bs-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-ink-light uppercase tracking-wide">Type</label>
            <div className="flex gap-2 mt-1">
              {["asset", "liability", "equity"].map((t) => (
                <button key={t} onClick={() => { setType(t); setCategory(""); }} data-testid={`bs-type-${t}`}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm capitalize border transition-colors ${
                    type === t ? "bg-forest text-white border-forest" : "border-border text-ink-light hover:bg-paper"
                  }`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-ink-light uppercase tracking-wide">Category</label>
            <input list="bs-cat-suggestions" value={category} onChange={(e) => setCategory(e.target.value)}
              placeholder={suggested[0] || "e.g., Fixed Assets"} className="input-base mt-1 w-full"
              data-testid="bs-category-input" />
            <datalist id="bs-cat-suggestions">
              {suggested.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div>
            <label className="text-xs font-medium text-ink-light uppercase tracking-wide">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Office equipment" className="input-base mt-1 w-full"
              data-testid="bs-name-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-ink-light uppercase tracking-wide">Amount ($)</label>
            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00" className="input-base mt-1 w-full"
              data-testid="bs-amount-input" />
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-6">
          <button onClick={onClose} className="btn-secondary" data-testid="bs-modal-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="bs-modal-save">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Add item
          </button>
        </div>
      </div>
    </div>
  );
}
